
import React, { useState } from 'react';
import { useEffect } from 'react';
import { MapPin, Calendar, Trophy, Users, Play, ChevronRight, Star, Mail, Phone, User, CreditCard } from 'lucide-react';
import tourApi, { registrationApi } from '../services/tourApi';
import { TourStop } from '../data/tourStops';
import { useLanguage } from '../contexts/LanguageContext';

const AfricaSurfTour = () => {
  const { t } = useLanguage();
  const [selectedStop, setSelectedStop] = useState(0);
  const [showRegistration, setShowRegistration] = useState(false);
  const [tourStops, setTourStops] = useState<TourStop[]>([]);
  const [formLoading, setFormLoading] = useState(false);
   const [formMessage, setFormMessage] = useState('');
const [featuredEvent, setFeaturedEvent] = useState<TourStop | null>(null);
const featuredStop = tourStops.find(stop => stop.status === 'featured');

  const [loading, setLoading] = useState(true);
  const [registrationData, setRegistrationData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    category: '',
    experience: '',
    terms: false
  });

  // Charger les données au montage du composant
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [stopsData, featuredData] = await Promise.all([
          tourApi.getTourStops(),
          tourApi.getFeaturedEvent()
        ]);
        
        setTourStops(stopsData);
        setFeaturedEvent(featuredData);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const highlights = [
    {
      title: 'Meilleure Performance',
      subtitle: 'Cherif Fall - Dakar',
      score: '9.2',
      description: 'Score parfait sur une vague de 6 pieds'
    },
    {
      title: 'Plus Jeune Participant',
      subtitle: 'Amina Diop - 16 ans',
      score: '16',
      description: 'Prometteuse surfeuse du Sénégal'
    },
    {
      title: 'Participation Record',
      subtitle: 'Taghazout, Maroc',
      score: '52',
      description: 'Surfeurs de 12 pays différents'
    }
  ];

  const rankings = [
    { position: 1, name: 'Cherif Fall', country: 'Sénégal', points: 2840 },
    { position: 2, name: 'Youssef Benali', country: 'Maroc', points: 2650 },
    { position: 3, name: 'Kwame Asante', country: 'Ghana', points: 2410 },
    { position: 4, name: 'Fatima El Mansouri', country: 'Maroc', points: 2280 },
    { position: 5, name: 'Amina Diop', country: 'Sénégal', points: 2120 },
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRegistrationData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

const handleRegistrationSubmit = (e) => {
  e.preventDefault();

  const submitRegistration = async () => {
    const featuredStop = tourStops.find(stop => stop.status === 'featured');

    if (!featuredStop?.id) {
      alert("Aucun événement 'featured' trouvé. Merci de réessayer plus tard.");
      console.warn("Tour stops:", tourStops);
      return;
    }

    try {
      setFormLoading(true);
      const response = await registrationApi.submitRegistration({
        ...registrationData,
        eventId: featuredStop.id,
        submittedAt: new Date().toISOString(),
      });

      console.log('Response API:', response);

      if (response.success) {
        setFormMessage(`Inscription envoyée avec succès ! Numéro de confirmation: ${response.confirmationNumber}`);

        // Reset form data
        setRegistrationData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          country: '',
          category: '',
          experience: '',
          terms: false,
        });

        // Delay closing the modal to show the success message
        setTimeout(() => {
          setFormMessage(''); // Clear message after closing modal
        }, 7000); // Show message for 3 seconds
      } else {
        console.error('Erreur API:', response);
        setFormMessage(response.message || 'Une erreur est survenue.');
        // Optionally keep modal open for error message
        setTimeout(() => setFormMessage(''), 5000); // Clear error message after 5 seconds
      }
    } catch (error) {
      console.error('Error submitting registration:', error);
      setFormMessage('Erreur lors de l\'inscription. Veuillez réessayer ou nous contacter.');
      // Optionally keep modal open for error message
      setTimeout(() => setFormMessage(''), 5000); // Clear error message after 5 seconds
    } finally {
      setFormLoading(false);
    }
  };

  submitRegistration();
};

  return (
    <div className="" id="tour-page">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-teal-800 to-blue-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://i.imgur.com/mrt24S8.jpeg?auto=compress&cs=tinysrgb&w=1600" 
            alt="Surf africain"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Africa Championship
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto mb-8">
           {t('tour.hero.subtitle')}
          </p>
          <div className="flex flex-wrap justify-center gap-8 text-center">
            <div className="bg-white/10 backdrop-blur-md px-6 py-4 rounded-lg">
              <div className="text-2xl font-bold text-orange-400">{tourStops.length}</div>
              <div className="text-sm text-gray-300"> {t('tour.hero.steps')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Event Section */}
      {featuredStop && (
        <section className="py-20 bg-white" id="featured-event">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center space-x-2 bg-african-500 text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Star className="h-4 w-4" />
                <span> {t('tour.featured.title')}</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-ocean-900 mb-4">
                Championnat d'Afrique des Nations - {featuredStop.location}
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Participer à notre prochain étape.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <img
                  src={featuredStop.image_url}
                  alt={`${featuredStop.location}, ${featuredStop.country}`}
                  className="w-full h-96 object-cover rounded-2xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent rounded-2xl"></div>
                <div className="absolute bottom-6 left-6 text-white">
                  <div className="flex items-center space-x-4 mb-2">
                    <Calendar className="h-5 w-5" />
                    <span className="font-semibold">{featuredStop.month} {featuredStop.year}</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <MapPin className="h-5 w-5" />
                    <span>{featuredStop.location}, {featuredStop.country}</span>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-gray-600 text-lg leading-relaxed mb-6">
                  {featuredStop.description_fr}
                </p>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-ocean-900">{featuredStop.participants}</div>
                    <div className="text-sm text-gray-600">Places disponibles</div>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="font-bold text-ocean-900 mb-3">Points forts :</h3>
                  <ul className="space-y-2">
                    {featuredStop.highlights_fr?.map((highlight, index) => (
                      <li key={index} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-african-500 rounded-full"></div>
                        <span className="text-gray-600">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>

               {/* <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => setShowRegistration(true)}
                    className="flex-1 bg-african-500 hover:bg-african-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 flex items-center justify-center space-x-2"
                    id="inscription"
                  >
                    <User className="h-5 w-5" />
                    <span>S'inscrire maintenant</span>
                  </button>
            
                </div>*/}

                
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Registration Modal */}
      {showRegistration && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-ocean-900">Inscription - Africa Surf Tour</h2>
                <button
                  onClick={() => setShowRegistration(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-gray-600 mt-2">{featuredStop?.location}, {featuredStop?.country} • {featuredStop?.dates}</p>
            </div>
          {formMessage && (
  <div
    className={`mb-6 p-4 rounded-lg flex justify-between items-center ${
      formMessage.includes('succès')
        ? 'bg-green-100 text-green-800 border border-green-200'
        : 'bg-red-100 text-red-800 border border-red-200'
    }`}
  >
    <span>{formMessage}</span>
    {!formMessage.includes('succès') && (
      <button
        onClick={() => setFormMessage('')}
        className="text-sm text-gray-600 hover:text-gray-800"
      >
        Fermer
      </button>
    )}
  </div>
)}
              
            <form onSubmit={handleRegistrationSubmit} className="p-6 space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold text-ocean-900 mb-4">Informations personnelles</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={registrationData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-african-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={registrationData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-african-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={registrationData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-african-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={registrationData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-african-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Surf Information */}
              <div>
                <h3 className="text-lg font-semibold text-ocean-900 mb-4">Informations surf</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pays *</label>
                    <select
                      name="country"
                      value={registrationData.country}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-african-500 focus:border-transparent"
                    >
                      <option value="">Sélectionnez votre pays</option>
                      <option value="senegal">Sénégal</option>
                      <option value="maroc">Maroc</option>
                      <option value="ghana">Ghana</option>
                      <option value="cotedivoire">Côte d'Ivoire</option>
                      <option value="liberia">Libéria</option>
                      <option value="southafrica">Afrique du Sud</option>
                      <option value="other">Pas dans la liste</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie *</label>
                    <select
                      name="category"
                      value={registrationData.category}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-african-500 focus:border-transparent"
                    >
                      <option value="">Sélectionnez une catégorie</option>
                      <option value="mens-open">Homme</option>
                      <option value="womens-open">Femme</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Niveau d'expérience *</label>
                    <select
                      name="experience"
                      value={registrationData.experience}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-african-500 focus:border-transparent"
                    >
                      <option value="">Sélectionnez votre niveau</option>
                      <option value="beginner">Débutant</option>
                      <option value="intermediate">Intermédiaire</option>
                      <option value="advanced">Avancé</option>
                      <option value="professional">Professionnel</option>
                    </select>
                  </div>
                </div>
              </div>



              {/* Terms */}
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  name="terms"
                  checked={registrationData.terms}
                  onChange={handleInputChange}
                  required
                  className="mt-1 h-4 w-4 text-african-500 focus:ring-african-500 border-gray-300 rounded"
                />
                <label className="text-sm text-gray-600">
                  J'accepte les <a href="#" className="text-african-500 hover:text-african-600">conditions générales</a> et 
                  le <a href="#" className="text-african-500 hover:text-african-600">règlement de la compétition</a> *
                </label>
              </div>

              {/* Submit Button */}
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setShowRegistration(false)}
                  className="flex-1 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-3 rounded-lg font-semibold transition-colors duration-300"
                >
                  Annuler
                </button>
              <button
              type="submit"
              disabled={formLoading}
              className={`flex-1 bg-african-500 hover:bg-african-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 ${
              formLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              >
              {formLoading ? 'Envoi en cours...' : 'Envoyer l\'inscription'}
              </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Interactive Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-african-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Chargement des événements...</p>
            </div>
          ) : (
            <>
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-ocean-900 mb-4">
               {t('tour.map.title')}
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
             {t('tour.map.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Tour Stops List */}
            <div className="lg:col-span-1 space-y-4">
              {tourStops.map((stop, index) => (
                <div
                  key={stop.id}
                  className={`p-4 rounded-xl cursor-pointer transition-all duration-300 ${
                    selectedStop === index
                      ? 'bg-gradient-to-r from-ocean-50 to-african-50 border-2 border-african-400'
                      : 'bg-white hover:bg-gray-50 border-2 border-transparent shadow-lg'
                  }`}
                  onClick={() => setSelectedStop(index)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-ocean-900">{stop.location}, {stop.country}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      stop.status === 'completed' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {stop.status === 'completed' ? 'Terminé' : 'À venir'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>{stop.month} {stop.year}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Users className="h-4 w-4" />
                      <span>{stop.participants}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Selected Stop Details */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="relative h-64">
                  <img
                    src={tourStops[selectedStop].image_url}
                    alt={tourStops[selectedStop].location}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="text-2xl font-bold mb-1">
                      {tourStops[selectedStop].location}, {tourStops[selectedStop].country}
                    </h3>
                    <p className="text-gray-200">{tourStops[selectedStop].spot}</p>
                  </div>
                 
                </div>
                <div className="p-6">
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {tourStops[selectedStop].description_fr}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <Calendar className="h-6 w-6 text-african-500 mx-auto mb-2" />
                      <div className="font-semibold text-ocean-900">{tourStops[selectedStop].dates}</div>
                      <div className="text-sm text-gray-600">Date</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <Users className="h-6 w-6 text-african-500 mx-auto mb-2" />
                      <div className="font-semibold text-ocean-900">{tourStops[selectedStop].participants}</div>
                      <div className="text-sm text-gray-600">Participants</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      {tourStops[selectedStop].winner_men ? (
                        <>
                          <Trophy className="h-6 w-6 text-african-500 mx-auto mb-2" />
                          <div className="font-semibold text-ocean-900">{tourStops[selectedStop].winner_men}</div>
                          <div className="text-sm text-gray-600">Vainqueur</div>
                        </>
                      ) : (
                        <>
                          <MapPin className="h-6 w-6 text-african-500 mx-auto mb-2" />
                          <div className="font-semibold text-ocean-900">À venir</div>
                          <div className="text-sm text-gray-600">Statut</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
            </>
          )}
        </div>
      </section>

      {/* Highlights */}
     {/*   <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-ocean-900 mb-4">
              Moments Forts
            </h2>
            <p className="text-gray-600 text-lg">
              Les performances remarquables du tour 2024
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {highlights.map((highlight, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 text-center">
                <div className="text-4xl font-bold text-african-500 mb-3">{highlight.score}</div>
                <h3 className="text-xl font-bold text-ocean-900 mb-2">{highlight.title}</h3>
                <p className="text-gray-600 font-medium mb-2">{highlight.subtitle}</p>
                <p className="text-gray-500 text-sm">{highlight.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
*/}

      
    </div>
  );
};

export default AfricaSurfTour;